// src/pages/Achats/components/AchatsPanel.jsx
import React from "react";
import {
  FaCopy,
  FaCreditCard,
  FaEuroSign,
  FaInbox,
  FaUser,
} from "react-icons/fa";

import {
  formatDateTimeFR,
  formatMoney,
  getBuyer,
  getBuyerName,
  getFormulaLabel,
  getFormulaTone,
  getPaymentStatusLabel,
  getReglementLabel,
  isPaidPurchase,
} from "../achatsUtils";

export default function AchatsPanel({ rows, loading, searchTerm }) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Liste des achats
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {rows.length} achat{rows.length > 1 ? "s" : ""} affiché
          {rows.length > 1 ? "s" : ""}.
        </p>
      </div>

      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState searchTerm={searchTerm} />
      ) : (
        <>
          <AchatsTable rows={rows} />
          <div className="grid grid-cols-1 gap-3 p-3 lg:hidden">
            {rows.map((achat) => (
              <AchatCard key={achat.id} achat={achat} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function AchatsTable({ rows }) {
  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
          <tr>
            <th className="px-5 py-4">Date</th>
            <th className="px-5 py-4">Utilisateur</th>
            <th className="px-5 py-4">Formule</th>
            <th className="px-5 py-4">Montant</th>
            <th className="px-5 py-4">Règlement</th>
            <th className="px-5 py-4">Stripe</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((achat) => {
            const user = getBuyer(achat);
            const paid = isPaidPurchase(achat);

            return (
              <tr
                key={achat.id}
                className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
              >
                <td className="whitespace-nowrap px-5 py-4 align-top">
                  <div className="font-bold text-slate-900 dark:text-white">
                    {formatDateTimeFR(achat.date_achat)}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Achat #{achat.id}
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <div className="flex items-start gap-3">
                    <Avatar name={getBuyerName(achat)} />

                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {getBuyerName(achat)}
                      </div>

                      <EmailCopy email={user.email} />

                      {achat.user_achat_id ? (
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          ID utilisateur : {achat.user_achat_id}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <FormulaBadge formule={achat.formule} />
                </td>

                <td className="whitespace-nowrap px-5 py-4 align-top">
                  <div className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-3 py-2 font-bold text-white dark:bg-white dark:text-slate-950">
                    <FaEuroSign className="text-xs opacity-70" />
                    {formatMoney(achat.prix_total)}
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <StatusBadge ok={paid}>
                    {getReglementLabel(achat)}
                  </StatusBadge>
                </td>

                <td className="px-5 py-4 align-top">
                  <StatusBadge
                    ok={String(achat.payment_status || "").toLowerCase() === "paid"}
                  >
                    {getPaymentStatusLabel(achat)}
                  </StatusBadge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AchatCard({ achat }) {
  const user = getBuyer(achat);
  const paid = isPaidPurchase(achat);

  return (
    <article className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <Avatar name={getBuyerName(achat)} />

        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Achat #{achat.id}
          </div>

          <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">
            {getBuyerName(achat)}
          </h3>

          <EmailCopy email={user.email} />
        </div>

        <div className="rounded-2xl bg-slate-950 px-3 py-2 text-right text-white dark:bg-white dark:text-slate-950">
          <div className="text-[10px] font-bold uppercase opacity-60">
            Montant
          </div>
          <div className="font-bold">{formatMoney(achat.prix_total)}</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InfoCard label="Date" value={formatDateTimeFR(achat.date_achat)} />
        <InfoCard label="Formule" custom={<FormulaBadge formule={achat.formule} />} />
        <InfoCard
          label="Règlement"
          custom={<StatusBadge ok={paid}>{getReglementLabel(achat)}</StatusBadge>}
        />
        <InfoCard
          label="Stripe"
          custom={
            <StatusBadge
              ok={String(achat.payment_status || "").toLowerCase() === "paid"}
            >
              {getPaymentStatusLabel(achat)}
            </StatusBadge>
          }
        />
      </div>
    </article>
  );
}

function Avatar({ name }) {
  const initials = String(name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-pink-500 text-sm font-bold text-white shadow-sm">
      {initials || <FaUser />}
    </div>
  );
}

function EmailCopy({ email }) {
  const copyEmail = async () => {
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
    } catch {
      console.warn("Impossible de copier l'email");
    }
  };

  if (!email) {
    return <div className="mt-1 text-sm text-slate-500">Email non renseigné</div>;
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <span className="break-all text-sm font-semibold text-slate-600 dark:text-slate-300">
        {email}
      </span>

      <button
        type="button"
        onClick={copyEmail}
        className="inline-flex items-center gap-1 rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        <FaCopy />
        Copier
      </button>
    </div>
  );
}

function FormulaBadge({ formule }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${getFormulaTone(
        formule
      )}`}
    >
      {getFormulaLabel(formule)}
    </span>
  );
}

function StatusBadge({ ok, children }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
        ok
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300"
          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      <FaCreditCard className="text-[10px]" />
      {children}
    </span>
  );
}

function InfoCard({ label, value, custom }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/50">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-sm font-bold text-slate-800 dark:text-slate-100">
        {custom || value || "—"}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3 p-4 sm:p-5">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="flex animate-pulse items-center gap-3 rounded-[24px] border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
        >
          <div className="h-11 w-11 rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-1/2 rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
          <div className="hidden h-9 w-28 rounded-2xl bg-slate-200 dark:bg-slate-800 sm:block" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ searchTerm }) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FaInbox />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        {searchTerm ? "Aucun achat trouvé" : "Aucun achat"}
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {searchTerm
          ? "Essayez avec un autre mot-clé ou réinitialisez les filtres."
          : "Les achats de l’application apparaîtront ici."}
      </p>
    </div>
  );
}